import React, { useState } from 'react';
import { escapeHtml } from '@/utils/htmlEscape';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft, Building2, MapPin, Phone, ClipboardList, Edit2, Home, Printer, Users, UserCheck, UserX, Clock, BedDouble } from 'lucide-react';
import RosterManagement from '@/components/RosterManagement';
import FamilyMemberRow from '@/components/FamilyMemberRow';
import { useClients } from '@/hooks/useClients';
import { useRooms } from '@/hooks/useRooms';
import { mockSiteClients } from '@/data/mockData';
import { format } from 'date-fns';
import { FacilityRoomsTab } from './FacilityRoomsTab';
import { BedCountSettings } from '@/components/bedcount/BedCountSettings';
import { ShiftTimeFramesSettings } from '@/components/settings/ShiftTimeFramesSettings';
import { ShiftReportFieldsSettings } from '@/components/settings/ShiftReportFieldsSettings';

interface FacilityProfileViewProps {
  facility: {
    id: string;
    name: string;
    address?: string;
    phone?: string;
    capacity?: number;
    is_family_shelter?: boolean;
    room_total?: number;
  };
  onBack: () => void;
  onEdit?: (facility: any) => void;
  onSelectClient?: (clientId: string) => void;
  isDemo?: boolean;
}

export function FacilityProfileView({ facility, onBack, onEdit, onSelectClient, isDemo = false }: FacilityProfileViewProps) {
  const [activeTab, setActiveTab] = useState('census');
  const { clients: dbClients } = useClients();
  const { rooms } = useRooms();
  
  // Get clients for this facility
  const allClients = isDemo 
    ? (mockSiteClients[facility.id as keyof typeof mockSiteClients] || [])
    : dbClients;
  
  const facilityClients = allClients.filter((c: any) => c.site_id === facility.id || c.siteId === facility.id);
  const checkedInClients = facilityClients.filter((c: any) => c.status === 'checked-in');
  const checkedOutClients = facilityClients.filter((c: any) => c.status === 'checked-out');
  const checkedInCount = checkedInClients.length;
  const checkedOutCount = checkedOutClients.length;
  const totalClients = facilityClients.length;
  const capacity = facility.capacity || 0;
  const available = capacity - checkedInCount;
  const occupancyRate = capacity ? Math.round((checkedInCount / capacity) * 100) : 0;

  // Compute pass count
  const now = new Date();
  const passCount = facilityClients.filter((c: any) => {
    if (c.passStart && c.passEnd) return now >= new Date(c.passStart) && now <= new Date(c.passEnd);
    if (c.pass_start && c.pass_end) return now >= new Date(c.pass_start) && now <= new Date(c.pass_end);
    return false;
  }).length;

  const getFacilityTypeLabel = () => {
    if (facility.is_family_shelter) return 'Family';
    if (facility.name?.toLowerCase().includes('women')) return 'Women';
    return 'Men';
  };

  const handlePrint = () => {
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    const rows = facilityClients.map((c: any) => {
      const room = rooms.find((r: any) => r.id === c.room_id);
      const name = c.name || `${c.first_name || ''} ${c.last_name || ''}`.trim();
      return `<tr><td>${escapeHtml(name)}</td><td>${escapeHtml(room?.room_number || 'N/A')}</td><td>${escapeHtml(c.status || '')}</td></tr>`;
    }).join('');
    printWindow.document.write(`<!DOCTYPE html><html><head><title>Facility Profile - ${escapeHtml(facility.name)}</title>
      <style>body{font-family:Arial,sans-serif;margin:30px;color:#000}h1{font-size:18pt;margin:0 0 4px}h2{font-size:12pt;font-weight:normal;color:#444;margin:0 0 4px}.header{text-align:center;border-bottom:2px solid #000;padding-bottom:12px;margin-bottom:20px}.date{font-size:10pt;color:#666}table{width:100%;border-collapse:collapse;margin-top:12px}th,td{padding:8px 12px;border:1px solid #ddd;text-align:left}th{background:#f5f5f5;font-weight:600}.stats{display:flex;gap:16px;margin-bottom:16px}.stat{flex:1;border:1px solid #ccc;padding:10px;text-align:center}.stat .num{font-size:18pt;font-weight:bold}.stat .label{font-size:9pt;color:#666}.footer{margin-top:30px;text-align:center;font-size:8pt;color:#666;border-top:1px solid #ccc;padding-top:8px}@media print{button{display:none}}</style></head><body>
      <div class="header"><h1>${escapeHtml(facility.name)}</h1><h2>Facility Report</h2><div class="date">${format(new Date(), 'EEEE, MMMM d, yyyy h:mm a')}</div></div>
      <div class="stats"><div class="stat"><div class="num">${checkedInCount}</div><div class="label">Checked In</div></div><div class="stat"><div class="num">${totalClients}</div><div class="label">Total Clients</div></div><div class="stat"><div class="num">${rooms.filter((r: any) => r.site_id === facility.id).length}</div><div class="label">Rooms</div></div><div class="stat"><div class="num">${passCount}</div><div class="label">On Pass</div></div></div>
      <table><thead><tr><th>Name</th><th>Room</th><th>Status</th></tr></thead><tbody>${rows}</tbody></table>
      <div class="footer">Confidential — For Internal Use Only</div></body></html>`);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => printWindow.print(), 300);
  };

  // Transform clients for FamilyMemberRow format
  const transformedClients = facilityClients.map((client: any) => {
    const room = rooms.find((r: any) => r.id === client.room_id);
    const displayName = client.name || `${client.first_name || ''} ${client.last_name || ''}`.trim();
    
    // Calculate age from date_of_birth
    let age: number | undefined;
    if (client.date_of_birth) {
      const birthDate = new Date(client.date_of_birth);
      const today = new Date();
      age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }
    }

    return {
      id: client.id,
      name: displayName || 'Unknown',
      age,
      room: room?.room_number || client.room || 'N/A',
      status: client.status || 'unbedded',
      caseWorker: client.caseWorker || undefined,
      isHeadOfHousehold: client.is_head_of_household,
      partnerName: client.partner_name,
      children: client.children as { name: string; age?: number }[] | undefined,
      photo_url: client.photo_url || undefined,
    };
  });

  return (
    <div className="space-y-4">
      {/* Header with Back Button */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={onBack} className="gap-2">
          <ArrowLeft className="h-4 w-4" />
          Back to Overview
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Left Sidebar - Facility Summary */}
        <div className="lg:col-span-1">
          <Card>
            <CardContent className="p-4 space-y-3">
              {/* Facility Icon */}
              <div className="flex justify-center">
                <div className="h-20 w-20 rounded-lg bg-primary/10 flex items-center justify-center">
                  {facility.is_family_shelter ? (
                    <Home className="h-10 w-10 text-primary" />
                  ) : (
                    <Building2 className="h-10 w-10 text-primary" />
                  )}
                </div>
              </div>

              {/* Facility Name & Type */}
              <div className="text-center">
                <h2 className="text-xl font-semibold">{facility.name}</h2>
                <Badge variant={facility.is_family_shelter ? 'default' : 'secondary'} className="mt-2">
                  {getFacilityTypeLabel()}
                </Badge>
              </div>

              {/* Occupancy Stats - Primary focus of sidebar */}
              <div className="space-y-3 pt-4 border-t">
                <div className="text-center pb-2">
                  <p className="text-3xl font-bold text-primary">{occupancyRate}%</p>
                  <p className="text-xs text-muted-foreground">Occupancy Rate</p>
                </div>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div>
                    <p className="text-lg font-semibold">{checkedInCount}</p>
                    <p className="text-xs text-muted-foreground">In</p>
                  </div>
                  <div>
                    <p className="text-lg font-semibold">{available}</p>
                    <p className="text-xs text-muted-foreground">Beds Open</p>
                  </div>
                  <div>
                    <p className="text-lg font-semibold">{capacity}</p>
                    <p className="text-xs text-muted-foreground">Beds</p>
                  </div>
                </div>
              </div>

              {/* Actions */}
              {onEdit && (
                <div className="pt-4 border-t">
                  <Button 
                    variant="outline" 
                    className="w-full gap-2"
                    onClick={() => onEdit(facility)}
                  >
                    <Edit2 className="h-4 w-4" />
                    Edit Facility
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Main Content Area */}
        <div className="lg:col-span-3">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-4 max-w-2xl">
              <TabsTrigger value="census" className="gap-2">
                <Users className="h-4 w-4" />
                Census
              </TabsTrigger>
              <TabsTrigger value="rooms" className="gap-2">
                <BedDouble className="h-4 w-4" />
                Rooms
              </TabsTrigger>
              <TabsTrigger value="details" className="gap-2">
                <Building2 className="h-4 w-4" />
                Details
              </TabsTrigger>
              <TabsTrigger value="settings" className="gap-2">
                <ClipboardList className="h-4 w-4" />
                Settings
              </TabsTrigger>
            </TabsList>

            {/* Census Tab */}
            <TabsContent value="census" className="mt-4 space-y-4">
              {/* Date Header with Print Button */}
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold">Daily Census</h3>
                  <p className="text-sm text-muted-foreground">{format(new Date(), 'EEEE, MMMM d, yyyy')}</p>
                </div>
                <Button variant="outline" size="sm" onClick={handlePrint} className="gap-2 print:hidden">
                  <Printer className="h-4 w-4" />
                  Print
                </Button>
              </div>

              {/* 4 Stat Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                        <UserCheck className="h-5 w-5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-green-600">{checkedInCount}</p>
                        <p className="text-xs text-muted-foreground">
                          {facility.is_family_shelter ? 'Families In' : 'Checked In'}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-red-100 flex items-center justify-center">
                        <UserX className="h-5 w-5 text-red-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-red-600">{checkedOutCount}</p>
                        <p className="text-xs text-muted-foreground">
                          {facility.is_family_shelter ? 'Families Out' : 'Checked Out'}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-amber-100 flex items-center justify-center">
                        <Users className="h-5 w-5 text-amber-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-amber-600">{totalClients}</p>
                        <p className="text-xs text-muted-foreground">
                          {facility.is_family_shelter ? 'Total People' : 'Total Clients'}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
                        <Clock className="h-5 w-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-purple-600">{passCount}</p>
                        <p className="text-xs text-muted-foreground">Pass</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Client List Table */}
              <Card>
                <CardContent className="p-0">
                  {transformedClients.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-12 text-center">
                      <Users className="h-12 w-12 text-muted-foreground mb-4" />
                      <h3 className="text-lg font-semibold mb-2">No Clients</h3>
                      <p className="text-sm text-muted-foreground">
                        No clients are currently assigned to this facility
                      </p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b bg-muted/50">
                            <th className="text-left p-4 font-medium text-muted-foreground w-[35%]">Name</th>
                            <th className="text-left p-4 font-medium text-muted-foreground w-[12%]">Room</th>
                            <th className="text-left p-4 font-medium text-muted-foreground w-[10%]">Age</th>
                            <th className="text-left p-4 font-medium text-muted-foreground w-[20%]">Case Manager</th>
                            <th className="text-left p-4 font-medium text-muted-foreground w-[23%]">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {transformedClients.map((client) => (
                            <FamilyMemberRow
                              key={client.id}
                              client={client}
                              siteId={facility.id}
                              onSelectClient={onSelectClient}
                            />
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            {/* Rooms Tab */}
            <TabsContent value="rooms" className="mt-4">
              <FacilityRoomsTab facilityId={facility.id} isDemo={isDemo} />
            </TabsContent>


            {/* Details Tab */}
            <TabsContent value="details" className="mt-4">
              <Card>
                <CardContent className="p-6">
                  <h3 className="text-lg font-semibold mb-4">Facility Configuration</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <MapPin className="h-4 w-4" />
                        <span className="text-sm">Address</span>
                      </div>
                      <p className="font-medium pl-6">{facility.address || 'Not specified'}</p>
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Phone className="h-4 w-4" />
                        <span className="text-sm">Phone</span>
                      </div>
                      <p className="font-medium pl-6">{facility.phone || 'Not specified'}</p>
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Building2 className="h-4 w-4" />
                        <span className="text-sm">Total Rooms</span>
                      </div>
                      <p className="font-medium pl-6">{(() => {
                        const facilityRooms = (rooms as any[]).filter((r) => r.site_id === facility.id);
                        if (facilityRooms.length === 0) return 'Not configured';
                        const keys = new Set<string>();
                        for (const r of facilityRooms) {
                          const type = r.room_type || 'single';
                          if (type === 'shared') {
                            const m = String(r.room_number).match(/^(\d+)/);
                            keys.add(`shared:${m ? m[1] : r.room_number}`);
                          } else {
                            keys.add(`${type}:${r.room_number}`);
                          }
                        }
                        return keys.size;
                      })()}</p>
                    </div>
                    <div className="space-y-1">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Users className="h-4 w-4" />
                        <span className="text-sm">Total Beds</span>
                      </div>
                      <p className="font-medium pl-6">{(() => {
                        const facilityRooms = (rooms as any[]).filter((r) => r.site_id === facility.id);
                        const beds = facilityRooms.reduce((sum, r) => sum + (r.capacity || 1), 0);
                        return beds || facility.capacity || 'Not configured';
                      })()}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="settings" className="mt-4 space-y-4">
              <ShiftTimeFramesSettings />
              <ShiftReportFieldsSettings />
              <BedCountSettings siteId={facility.id} />
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}

export default FacilityProfileView;
